import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import type {
  ManualCheck,
  CreateManualCheckPayload,
} from '../../types/manualCheck';

interface Salesman {
  id: number;
  name: string;
}

interface ManualCheckFormProps {
  salesmen: Salesman[];
  firmSuggestions: string[];
  initial?: ManualCheck | null;
  onSaved: () => void;
  onCancel?: () => void;
}

/**
 * R16 Task A: Entry form for a manual check.
 *
 * Fields per Peter's spec: Date, Firm (with datalist autocomplete of prior
 * firm names), Amount, Salesman, Payout% (default 20), Memo (optional).
 *
 * Snapshot columns (salesman_commission / house_commission) are recomputed
 * server-side by the model's saving hook — the form only submits source
 * fields.
 */
export const ManualCheckForm: React.FC<ManualCheckFormProps> = ({
  salesmen,
  firmSuggestions,
  initial,
  onSaved,
  onCancel,
}) => {
  const today = new Date().toISOString().split('T')[0];

  const [checkDate, setCheckDate] = useState<string>(initial?.check_date ?? today);
  const [firmName, setFirmName] = useState<string>(initial?.firm_name ?? '');
  const [amount, setAmount] = useState<string>(
    initial?.amount != null ? String(initial.amount) : ''
  );
  const [salesmanId, setSalesmanId] = useState<number | ''>(
    initial?.salesman_user_id ?? ''
  );
  const [payoutPct, setPayoutPct] = useState<string>(
    initial?.payout_percentage != null ? String(initial.payout_percentage) : '20'
  );
  const [memo, setMemo] = useState<string>(initial?.memo ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // R18 hotfix: set when the API refuses an identical check (409).
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

  // R16 code-review finding #9: only resync local state when the identity
  // of the check being edited actually changes. The previous
  // `useEffect([initial])` fired on every parent re-render — a parent that
  // called `loadChecks()` after saving would silently wipe an in-progress
  // edit on a different row because it created a new `initial` reference
  // for the same id. Tracking `initial?.id` via a ref makes the effect a
  // true "row switched" trigger; edits stay put during unrelated renders.
  const prevInitialIdRef = useRef<number | undefined>(initial?.id);
  useEffect(() => {
    if (initial?.id !== prevInitialIdRef.current) {
      if (initial) {
        setCheckDate(initial.check_date);
        setFirmName(initial.firm_name);
        setAmount(String(initial.amount));
        setSalesmanId(initial.salesman_user_id);
        setPayoutPct(String(initial.payout_percentage));
        setMemo(initial.memo ?? '');
      }
      prevInitialIdRef.current = initial?.id;
    }
  }, [initial]);

  const handleSubmit = async (e: React.FormEvent, allowDuplicate = false) => {
    e.preventDefault();
    setError(null);
    if (!allowDuplicate) setDuplicateWarning(null);

    const parsedAmount = parseFloat(amount);
    const parsedPct = parseFloat(payoutPct);

    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Amount must be greater than 0.');
      return;
    }
    if (Number.isNaN(parsedPct) || parsedPct < 0 || parsedPct > 100) {
      setError('Payout percentage must be between 0 and 100.');
      return;
    }
    if (!salesmanId) {
      setError('Salesman is required.');
      return;
    }

    const payload: CreateManualCheckPayload = {
      check_date: checkDate,
      firm_name: firmName.trim(),
      amount: parsedAmount,
      salesman_user_id: Number(salesmanId),
      payout_percentage: parsedPct,
      memo: memo.trim() || null,
      ...(allowDuplicate ? { allow_duplicate: true } : {}),
    };

    setSubmitting(true);
    try {
      if (initial?.id) {
        await api.updateManualCheck(initial.id, payload);
      } else {
        await api.createManualCheck(payload);
      }
      // Reset form (only when creating; edit-in-place stays populated so
      // parent can control lifecycle)
      if (!initial) {
        setFirmName('');
        setAmount('');
        setMemo('');
      }
      setDuplicateWarning(null);
      onSaved();
    } catch (err: any) {
      // R18 hotfix: 409 means an identical check already exists for this
      // date, firm, amount and salesman. Offer to record it anyway rather
      // than dead-ending -- a firm can genuinely send two identical
      // cheques on one day.
      if (err?.response?.status === 409) {
        setDuplicateWarning(
          err?.response?.data?.message ||
            'A matching check already exists for this date.'
        );
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to save manual check'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const previewSalesman =
    !Number.isNaN(parseFloat(amount)) && !Number.isNaN(parseFloat(payoutPct))
      ? (parseFloat(amount) * parseFloat(payoutPct)) / 100
      : 0;
  const previewHouse =
    !Number.isNaN(parseFloat(amount)) && !Number.isNaN(parseFloat(payoutPct))
      ? parseFloat(amount) - previewSalesman
      : 0;

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white shadow rounded-lg p-4 space-y-4"
    >
      <h3 className="text-lg font-semibold text-gray-900">
        {initial ? 'Edit Manual Check' : 'Record Manual Check'}
      </h3>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {/* R18 hotfix: an identical check already exists for this date, firm,
          amount and salesman. On 2026-10-19 a $10,000 Rokos Capital check
          was entered twice, inflating that day's blotter by $10,000. Warn
          rather than block — a firm can genuinely send two identical
          cheques on one day. */}
      {duplicateWarning && (
        <div
          role="alert"
          className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2"
        >
          <p className="font-medium">Possible duplicate</p>
          <p className="mt-1">{duplicateWarning}</p>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              disabled={submitting}
              onClick={(e) => handleSubmit(e, true)}
              className="px-3 py-1 rounded bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              Record it anyway
            </button>
            <button
              type="button"
              onClick={() => setDuplicateWarning(null)}
              className="text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm text-gray-700">Date</span>
          <input
            type="date"
            value={checkDate}
            onChange={(e) => setCheckDate(e.target.value)}
            className="mt-1 w-full border rounded px-3 py-2"
            required
          />
        </label>

        <label className="block">
          <span className="text-sm text-gray-700">Firm</span>
          <input
            type="text"
            list="manual-check-firms"
            value={firmName}
            onChange={(e) => setFirmName(e.target.value)}
            placeholder="e.g. Acme Capital"
            className="mt-1 w-full border rounded px-3 py-2"
            required
          />
          {/* datalist provides autocomplete of prior firm names, no external
              dep required. Works in all evergreen browsers. */}
          <datalist id="manual-check-firms">
            {firmSuggestions.map((firm) => (
              <option key={firm} value={firm} />
            ))}
          </datalist>
        </label>

        <label className="block">
          <span className="text-sm text-gray-700">Amount ($)</span>
          <input
            type="number"
            step="0.0001"
            min="0.0001"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full border rounded px-3 py-2"
            required
          />
        </label>

        <label className="block">
          <span className="text-sm text-gray-700">Salesman</span>
          <select
            value={salesmanId}
            onChange={(e) => setSalesmanId(e.target.value ? Number(e.target.value) : '')}
            className="mt-1 w-full border rounded px-3 py-2"
            required
          >
            <option value="">-- select salesman --</option>
            {salesmen.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm text-gray-700">Payout %</span>
          <input
            type="number"
            step="0.0001"
            min="0"
            max="100"
            value={payoutPct}
            onChange={(e) => setPayoutPct(e.target.value)}
            className="mt-1 w-full border rounded px-3 py-2"
            required
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="text-sm text-gray-700">Memo (optional)</span>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            rows={2}
            className="mt-1 w-full border rounded px-3 py-2"
          />
        </label>
      </div>

      <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
        Preview → Salesman commission: <b>${previewSalesman.toFixed(2)}</b>
        {' · '}House commission: <b>${previewHouse.toFixed(2)}</b>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : initial ? 'Update Check' : 'Record Check'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default ManualCheckForm;
